import { tWithKeyAttr as t } from "../../i18nHelper";
import React, { ReactNode, useState } from "react";
import styles from "./clinicalFindingsPanel.module.css";
import i18n from "../../i18n";
import clockIcon from "../../images/clock_icon.svg";
import warningIcon from "../../images/warning_icom.svg";
import { useGetClinicalFindingsQuery } from "../../services/api/user.api";
import {
  TMedicationFinding,
  TMedicationFlag,
  TMedicationMention,
  TRecommendationItem,
  TRecommendationsFinding,
  TRiskConcern,
  TRiskFinding,
  TSuicidalityFinding,
} from "../../services/types/user.types";
import { getPrintDate } from "../../utils/utils";
import Loader from "../loader/loader";

const localizeEnum = (prefix: string, value: string | null | undefined): string | null => {
  if (!value) return null;
  return t(`${prefix}_${value.toUpperCase()}`, { defaultValue: value });
};

// Per spec: shown only when the client was asked and did not answer; "answered" renders nothing.
const localizeClientResponse = (value: string | null | undefined): string | null => {
  if (value !== "not_answered") return null;
  return localizeEnum("CLINICAL_RESPONSE", value);
};

const isDifferentFromUiLanguage = (fieldLanguage: string | null | undefined): boolean => {
  if (!fieldLanguage) return false;
  const current = (i18n.language || "").split("-")[0].toLowerCase();
  const field = fieldLanguage.split("-")[0].toLowerCase();
  return current !== field;
};

// Color follows the score only; the alert is shown by its icon. The spec sets alert on every
// suicidality score above 0, so coloring by alert would make 1, 2 and 3 look the same.
// Suicidality: 0 none, 1 passive, 2 active, 3 with plan. Risk: 0 none, 1 needs review, 2 concerning.
const severityClass = (value: number | null, max: number): string => {
  if (value == null) return styles.severityHigh; // alert with no usable score: assume the worst
  if (value === 0) return styles.severityNone;
  if (value === max) return styles.severityHigh;
  if (value === 1) return styles.severityLow;
  return styles.severityMedium;
};

const isValidScore = (value: number | null, max: number): value is number =>
  value != null && Number.isInteger(value) && value >= 0 && value <= max;

// A missing or out-of-range score (e.g. 5/3) is not shown, but an alert must always stay visible.
export const SeverityBadge = ({
  value,
  max,
  alert,
}: {
  value: number | null;
  max: number;
  alert: boolean;
}) => {
  const hasScore = isValidScore(value, max);
  if (!hasScore && !alert) return null;
  return (
    <span className={`${styles.severityBadge} ${severityClass(hasScore ? value : null, max)}`}>
      {alert && <img src={warningIcon} alt={t("CLINICAL_FINDINGS_ALERT")} className={styles.alertIcon} />}
      {hasScore && `${value}/${max}`}
    </span>
  );
};

const FindingField = ({ label, value }: { label: string; value: string | null | undefined }) => {
  if (!value) return null;
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={styles.fieldValue}>{value}</span>
    </div>
  );
};

// The backend's raisedBy strings ("Raised by the clinician"/"...client") are
// already complete phrases, so no separate label is rendered alongside them.
const PlainField = ({ value }: { value: string | null | undefined }) => {
  if (!value) return null;
  return (
    <div className={styles.field}>
      <span className={styles.fieldValue}>{value}</span>
    </div>
  );
};

const QuotedPhrase = ({ label, value }: { label: string; value: string | null | undefined }) => {
  if (!value) return null;
  return (
    <div className={styles.quoteBlock}>
      <span className={styles.fieldLabel}>{label}</span>
      <p className={styles.quoteText}>{value}</p>
    </div>
  );
};

const StatusLine = ({
  statusLine,
  statusLineLanguage,
}: {
  statusLine: string | null;
  statusLineLanguage: string | null;
}) => {
  if (!statusLine) return null;
  return (
    <div className={styles.statusLineBlock}>
      <p className={styles.statusLineText}>{statusLine}</p>
      {isDifferentFromUiLanguage(statusLineLanguage) && (
        <span className={styles.languageTag}>
          {t("LANGUAGE")}: {statusLineLanguage}
        </span>
      )}
    </div>
  );
};

// Shared shell for every findings card: header with title (and optional badge), then details or an empty state.
// Children render below the details regardless of whether a finding exists (e.g. the history toggle).
export const FindingCard = <T,>({
  title,
  finding,
  emptyText,
  badge,
  render,
  children,
}: {
  title: string;
  finding: T | null;
  emptyText: string;
  badge?: ReactNode;
  render: (finding: T) => ReactNode;
  children?: ReactNode;
}) => (
  <div className={styles.card}>
    <div className={styles.cardHeader}>
      <h3 className={styles.cardTitle}>{title}</h3>
      {badge}
    </div>
    {finding ? render(finding) : <p className={styles.emptyState}>{emptyText}</p>}
    {children}
  </div>
);

const FindingMeta = ({
  recordNumber,
  createdAt,
  promptVersion,
}: {
  recordNumber: number | null;
  createdAt: string;
  promptVersion: string | null;
}) => {
  const createdDate = new Date(createdAt);
  const hasValidDate = !Number.isNaN(createdDate.getTime());
  return (
    <div className={styles.metaRow}>
      {recordNumber != null && <span>{t("MEETING_NUMBER")} {recordNumber}</span>}
      {hasValidDate && <span>{getPrintDate(createdDate)}</span>}
      {promptVersion && <span className={styles.promptVersionTag}>{promptVersion}</span>}
    </div>
  );
};

const RiskConcernCard = ({ concern }: { concern: TRiskConcern }) => {
  const clientResponse = localizeClientResponse(concern.clientResponse);
  return (
    <div className={styles.concernCard}>
      <div className={styles.concernHeader}>
        {localizeEnum("CLINICAL_RISK_CATEGORY", concern.category)}
      </div>
      {concern.category === "impaired_judgment_or_reality_testing" && (
        <FindingField
          label={t("CLINICAL_FINDINGS_BASIS")}
          value={localizeEnum("CLINICAL_RISK_BASIS", concern.basis)}
        />
      )}
      <QuotedPhrase
        label={t("CLINICAL_FINDINGS_SOURCE_PHRASE")}
        value={concern.sourcePhrase}
      />
      <FindingField
        label={t("CLINICAL_FINDINGS_EVIDENCE_BASIS")}
        value={localizeEnum("CLINICAL_EVIDENCE", concern.evidenceBasis)}
      />
      <PlainField value={localizeEnum("CLINICAL_RAISED_BY", concern.raisedBy)} />
      <FindingField
        label={t("CLINICAL_FINDINGS_CLIENT_RESPONSE")}
        value={clientResponse}
      />
    </div>
  );
};

export const SuicidalityFindingDetails = ({ finding }: { finding: TSuicidalityFinding }) => (
  <>
    <FindingMeta
      recordNumber={finding.recordNumber}
      createdAt={finding.createdAt}
      promptVersion={finding.promptVersion}
    />
    <StatusLine statusLine={finding.statusLine} statusLineLanguage={finding.statusLineLanguage} />
    <FindingField
      label={t("CLINICAL_FINDINGS_TEMPORAL_CONTEXT")}
      value={localizeEnum("CLINICAL_TEMPORAL", finding.temporalContext)}
    />
    <QuotedPhrase
      label={t("CLINICAL_FINDINGS_SOURCE_PHRASE")}
      value={finding.sourcePhrase}
    />
    <FindingField
      label={t("CLINICAL_FINDINGS_EVIDENCE_BASIS")}
      value={localizeEnum("CLINICAL_EVIDENCE", finding.evidenceBasis)}
    />
    <PlainField value={localizeEnum("CLINICAL_RAISED_BY", finding.raisedBy)} />
    <FindingField
      label={t("CLINICAL_FINDINGS_CLIENT_RESPONSE")}
      value={localizeClientResponse(finding.clientResponse)}
    />
    <FindingField
      label={t("CLINICAL_FINDINGS_ABSENCE_REASON")}
      value={localizeEnum("CLINICAL_ABSENCE", finding.absenceReason)}
    />
    <FindingField label={t("NOTES")} value={finding.extractionNotes} />
  </>
);

const SuicidalityCard = ({
  finding,
  history,
}: {
  finding: TSuicidalityFinding | null;
  history: TSuicidalityFinding[];
}) => {
  const [showHistory, setShowHistory] = useState(false);
  const pastHistory = history.filter((item) => item.recordNumber !== finding?.recordNumber);

  return (
    <FindingCard
      title={t("CLINICAL_FINDINGS_SUICIDALITY")}
      finding={finding}
      emptyText={t("CLINICAL_FINDINGS_NO_DATA")}
      badge={finding && <SeverityBadge value={finding.value} max={3} alert={finding.alert} />}
      render={(f) => <SuicidalityFindingDetails finding={f} />}
    >
      {pastHistory.length > 0 && (
        <button type="button" className={styles.historyButton} onClick={() => setShowHistory((prev) => !prev)}>
          <img src={clockIcon} alt="" className={styles.historyIcon} />
          {showHistory ? t("HISTORY_VIEW_CURRENT") : t("HISTORY_VIEW_HISTORY")}
        </button>
      )}

      {showHistory && pastHistory.length > 0 && (
        <div className={styles.historyContainer}>
          {pastHistory.map((item, idx) => (
            <div key={item.recordNumber ?? `no-record-${idx}`} className={styles.historyItem}>
              <FindingMeta
                recordNumber={item.recordNumber}
                createdAt={item.createdAt}
                promptVersion={item.promptVersion}
              />
              <StatusLine statusLine={item.statusLine} statusLineLanguage={item.statusLineLanguage} />
            </div>
          ))}
        </div>
      )}
    </FindingCard>
  );
};

export const RiskFindingDetails = ({ finding }: { finding: TRiskFinding }) => (
  <>
    <FindingMeta
      recordNumber={finding.recordNumber}
      createdAt={finding.createdAt}
      promptVersion={finding.promptVersion}
    />
    <StatusLine statusLine={finding.statusLine} statusLineLanguage={finding.statusLineLanguage} />

    {finding.concerns.length > 0 ? (
      <div className={styles.concernsList}>
        {finding.concerns.map((concern, idx) => (
          <RiskConcernCard key={idx} concern={concern} />
        ))}
      </div>
    ) : (
      <>
        <FindingField
          label={t("CLINICAL_FINDINGS_ABSENCE_REASON")}
          value={localizeEnum("CLINICAL_ABSENCE", finding.absenceReason)}
        />
        <QuotedPhrase
          label={t("CLINICAL_FINDINGS_SOURCE_PHRASE")}
          value={finding.absencePhrase}
        />
        <PlainField value={localizeEnum("CLINICAL_RAISED_BY", finding.raisedBy)} />
        <FindingField
          label={t("CLINICAL_FINDINGS_CLIENT_RESPONSE")}
          value={localizeClientResponse(finding.clientResponse)}
        />
      </>
    )}
    <FindingField label={t("NOTES")} value={finding.extractionNotes} />
  </>
);

const RiskCard = ({
  finding,
  history,
}: {
  finding: TRiskFinding | null;
  history: TRiskFinding[];
}) => {
  const [showHistory, setShowHistory] = useState(false);
  const pastHistory = history.filter((item) => item.recordNumber !== finding?.recordNumber);

  return (
    <FindingCard
      title={t("RISK")}
      finding={finding}
      emptyText={t("CLINICAL_FINDINGS_NO_DATA")}
      badge={finding && <SeverityBadge value={finding.value} max={2} alert={finding.alert} />}
      render={(f) => <RiskFindingDetails finding={f} />}
    >
      {pastHistory.length > 0 && (
        <button type="button" className={styles.historyButton} onClick={() => setShowHistory((prev) => !prev)}>
          <img src={clockIcon} alt="" className={styles.historyIcon} />
          {showHistory ? t("HISTORY_VIEW_CURRENT") : t("HISTORY_VIEW_HISTORY")}
        </button>
      )}

      {showHistory && pastHistory.length > 0 && (
        <div className={styles.historyContainer}>
          {pastHistory.map((item, idx) => (
            <div key={item.recordNumber ?? `no-record-${idx}`} className={styles.historyItem}>
              <FindingMeta
                recordNumber={item.recordNumber}
                createdAt={item.createdAt}
                promptVersion={item.promptVersion}
              />
              <StatusLine statusLine={item.statusLine} statusLineLanguage={item.statusLineLanguage} />
              {item.concerns.map((concern, idx) => (
                <RiskConcernCard key={idx} concern={concern} />
              ))}
            </div>
          ))}
        </div>
      )}
    </FindingCard>
  );
};

const MedicationMentionCard = ({
  mention,
  statusLineLanguage,
}: {
  mention: TMedicationMention;
  statusLineLanguage: string | null;
}) => {
  // matchedIngredient is often "not_sure", so it is only an extra hint next to the words used.
  const ingredient =
    mention.matchedIngredient && mention.matchedIngredient !== "not_sure" ? mention.matchedIngredient : null;
  const resolution = [
    localizeEnum("CLINICAL_MED_RESOLUTION", mention.resolution),
    localizeEnum("CLINICAL_MED_TEMPORAL", mention.temporalStatus),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className={styles.concernCard}>
      {(mention.verbatimPhrase || ingredient) && (
        <div className={styles.concernHeader}>
          {mention.verbatimPhrase}
          {ingredient && ` (${ingredient})`}
        </div>
      )}
      <StatusLine statusLine={mention.statusLine} statusLineLanguage={statusLineLanguage} />
      <PlainField value={resolution} />
      <QuotedPhrase
        label={t("CLINICAL_FINDINGS_SOURCE_PHRASE")}
        value={mention.sourcePhrase}
      />
      <FindingField
        label={t("CLINICAL_FINDINGS_EVIDENCE_BASIS")}
        value={localizeEnum("CLINICAL_EVIDENCE", mention.evidenceBasis)}
      />
      <PlainField value={localizeEnum("CLINICAL_RAISED_BY", mention.raisedBy)} />
      <FindingField
        label={t("CLINICAL_FINDINGS_CLIENT_RESPONSE")}
        value={localizeClientResponse(mention.clientResponse)}
      />
    </div>
  );
};

// "not_applicable" is its own state (not a "no"), so the status is always shown, with the notes explaining it.
const MedicationFlagSection = ({
  title,
  flag,
  statusLineLanguage,
}: {
  title: string;
  flag: TMedicationFlag;
  statusLineLanguage: string | null;
}) => (
  <div className={styles.subSection}>
    <div className={styles.subSectionHeader}>
      <h4 className={styles.subSectionTitle}>{title}</h4>
      {flag.mentionStatus && (
        <span className={styles.statusTag}>{localizeEnum("CLINICAL_MED_STATUS", flag.mentionStatus)}</span>
      )}
    </div>
    {flag.mentions.length > 0 && (
      <div className={styles.concernsList}>
        {flag.mentions.map((mention, idx) => (
          <MedicationMentionCard key={idx} mention={mention} statusLineLanguage={statusLineLanguage} />
        ))}
      </div>
    )}
    <FindingField label={t("NOTES")} value={flag.extractionNotes} />
  </div>
);

export const MedicationFindingDetails = ({ finding }: { finding: TMedicationFinding }) => (
  <>
    <FindingMeta
      recordNumber={finding.recordNumber}
      createdAt={finding.createdAt}
      promptVersion={finding.promptVersion}
    />
    <MedicationFlagSection
      title={t("CLINICAL_MED_PRESCRIBED")}
      flag={finding.prescribed}
      statusLineLanguage={finding.statusLineLanguage}
    />
    <MedicationFlagSection
      title={t("CLINICAL_MED_SELF_DIRECTED")}
      flag={finding.selfDirected}
      statusLineLanguage={finding.statusLineLanguage}
    />
  </>
);

const RecommendationItemCard = ({ item }: { item: TRecommendationItem }) => {
  // "other" has no locale entry of its own — the model's label is the only name it has.
  const category =
    item.category === "other" ? item.categoryLabel : localizeEnum("CLINICAL_REC_CATEGORY", item.category);

  return (
    <div className={styles.concernCard}>
      {category && <div className={styles.concernHeader}>{category}</div>}
      {item.recommendationLine && <p className={styles.recommendationLine}>{item.recommendationLine}</p>}
      {item.suggestedNeedLabel && <p className={styles.suggestion}>💡 {item.suggestedNeedLabel}</p>}
      <QuotedPhrase
        label={t("CLINICAL_FINDINGS_SOURCE_PHRASE")}
        value={item.sourcePhrase}
      />
      <FindingField
        label={t("CLINICAL_FINDINGS_EVIDENCE_BASIS")}
        value={localizeEnum("CLINICAL_EVIDENCE", item.evidenceBasis)}
      />
      <PlainField value={localizeEnum("CLINICAL_RAISED_BY", item.raisedBy)} />
      <FindingField
        label={t("CLINICAL_FINDINGS_CLIENT_RESPONSE")}
        value={localizeClientResponse(item.clientResponse)}
      />
    </div>
  );
};

export const RecommendationsFindingDetails = ({ finding }: { finding: TRecommendationsFinding }) => (
  <>
    <FindingMeta
      recordNumber={finding.recordNumber}
      createdAt={finding.createdAt}
      promptVersion={finding.promptVersion}
    />
    {finding.items.length > 0 ? (
      [1, 2].map((tier) => {
        const tierItems = finding.items.filter((item) => item.tier === tier);
        if (tierItems.length === 0) return null;
        return (
          <div key={tier} className={styles.subSection}>
            <h4 className={styles.subSectionTitle}>{t(`CLINICAL_REC_TIER_${tier}`)}</h4>
            <div className={styles.concernsList}>
              {tierItems.map((item, idx) => (
                <RecommendationItemCard key={idx} item={item} />
              ))}
            </div>
          </div>
        );
      })
    ) : (
      <StatusLine statusLine={finding.statusLine} statusLineLanguage={finding.statusLineLanguage} />
    )}
    <FindingField label={t("NOTES")} value={finding.extractionNotes} />
  </>
);

// A failed request must never look like "no findings" — for suicidality that reads as "nothing was found".
export const ClinicalFindingsLoadError = ({ onRetry }: { onRetry: () => void }) => (
  <div className={styles.errorState}>
    <p role="alert">{t("CLINICAL_FINDINGS_LOAD_ERROR")}</p>
    <button type="button" className={styles.historyButton} onClick={onRetry}>
      {t("RETRY")}
    </button>
  </div>
);

const ClinicalFindingsPanel = ({ userGuid }: { userGuid: string | undefined }) => {
  // currentData, not data: data keeps the previous patient's findings until the new request finishes.
  // isFetching, not isLoading: isLoading stays false on a retry after an error and when switching
  // patients, which would briefly show "No findings yet" instead of a loader.
  const { currentData, isFetching, isError, refetch } = useGetClinicalFindingsQuery(userGuid ?? "", {
    skip: !userGuid,
  });

  if (!userGuid) return null;

  return (
    <section className={styles.panel} aria-label={t("CLINICAL_FINDINGS")}>
      <h2 className={styles.panelTitle}>
        {t("CLINICAL_FINDINGS")}
      </h2>
      {isFetching ? (
        <Loader />
      ) : isError ? (
        <ClinicalFindingsLoadError onRetry={refetch} />
      ) : (
        <div className={styles.grid}>
          <SuicidalityCard
            finding={currentData?.latestSuicidality ?? null}
            history={currentData?.suicidalityHistory ?? []}
          />
          <RiskCard finding={currentData?.latestRisk ?? null} history={currentData?.riskHistory ?? []} />
        </div>
      )}
    </section>
  );
};

export default ClinicalFindingsPanel;
